import React from 'react';
import _ from 'lodash';

import {
  fields,
  Form,
  LoadingOverlay,
  modalUtil,
  SectionHeaderBox,
  Table
} from 'iron-forge-react-modules';
import {
  activityTemplates,
  appFormUtil,
  isProd,
  OrderStatusChip,
  routeDirectory,
  selectFields,
  SkuSwapModal,
  StatusCheckIcon
} from 'modules';
import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  styled
} from '@mui/material';
import {
  useNavigate,
  useParams
} from 'react-router-dom';
import {
  OrderStatusName,
  SalesChannelName
} from 'generated/graphql';

import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import * as types from './types';
import * as dataService from './dataService';



const TableComponent = Table.genTableComponent<types.IColumnId>();

const DateFieldController = fields.DateField.genControllerComponent<types.IFormState>();

const OrderStatusChipsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: theme.spacing(1)
}));

const FormContainerBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',

  '& form': {
    width: '100%'
  }
}));

const DateContainerBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: '100%'
}));

const SecondRowBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(2)
}));

const ButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2)
}));

const TextFieldController = fields.TextField.genControllerComponent<types.IFormState>();



export const ReconciliationReportActivity: React.FC = () => {
  const params = useParams<{ salesChannel: SalesChannelName; }>();
  const StoreSelectFieldController = React.useMemo(() => {
    const { genController } = selectFields.StoreSelectField.genComponentAndControllerGenerator({ salesChannel: params.salesChannel });
    return genController<types.IFormState>();
  }, [params.salesChannel]);
  const PaymentMethodSelectFieldController = React.useMemo(() => {
    const { genController } = selectFields.PaymentMethodSelectField.genComponentAndControllerGenerator();
    return genController<types.IFormState>();
  }, []);
  const LeaseApplicationStepNameSelectFieldController = React.useMemo(() => {
    const { genController } = selectFields.LeaseApplicationStepNameField.genComponentAndControllerGenerator();
    return genController<types.IFormState>();
  }, []);
  const navigate = useNavigate();
  const [selectedOrderStatuses, setSelectedOrderStatuses] = React.useState<OrderStatusName[]>(['completed', 'onHold', 'pending', 'processing']);
  const {
    fnQuery,
    queryTuple,
    fnQueryPage,
    pageInfo,
    fnExport
  } = dataService.useQuery(params.salesChannel ?? 'ecommerce');
  const filterQueryTuple = dataService.useFilterQuery();
  const [fnWriteOrderToE1, writeOrderToE1Tuple] = dataService.useWriteOrderToE1();
  const [fnCloseOrder, closeOrderTuple] = dataService.useCloseOrder();
  const [fnMockFulfill, mockFulfillTuple] = dataService.useMockFulfillOrder();
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['skuSwap']);
  const [selectedOrderId, setSelectedOrderId] = React.useState('');



  const form = useForm<types.IFormState>({
    defaultValues: {
      omniFreeText: '',
      after: DateTime.now().minus({ days: 3 }).startOf('day').toJSDate(),
      before: DateTime.now().endOf('day').toJSDate()
    }
  });



  function handleClickPopoverOption(rowId: string, optionId: string) {
    if (optionId === 'writeToE1') {
      fnWriteOrderToE1(rowId);
    } else if (optionId === 'closeOrder') {
      fnCloseOrder(rowId);
    } else if (optionId === 'mockFulfill') {
      fnMockFulfill(rowId);
    } else if (optionId === 'skuSwap') {
      setModalIsOpen('skuSwap', true);
      setSelectedOrderId(rowId);
    }
  }



  function handleClickExport() {
    fnExport();
  }



  function handleClick3plImports() {
    navigate(routeDirectory.admin.thirdPartyLogistics.path());
  }



  function handleSelectOrderStatus(name: OrderStatusName) {
    if (_.includes(selectedOrderStatuses, name)) {
      setSelectedOrderStatuses(_.filter(selectedOrderStatuses, status =>
        status !== name)
      );

    } else {
      setSelectedOrderStatuses([
        ...selectedOrderStatuses,
        name
      ]);
    }
  }



  function processSubmit(formState: types.IFormState) {
    const data = appFormUtil.trimFormStateValues(formState);
    fnQuery({
      filter: {
        salesChannel: params.salesChannel ?? 'ecommerce',
        freeText: data.omniFreeText,
        storeId: data.store?.id,
        statuses: selectedOrderStatuses,
        before: DateTime.fromJSDate(data.before).endOf('day'),
        after: DateTime.fromJSDate(data.after).startOf('day'),
        paymentMethodName: data.paymentMethod?.id,
        leaseApplicationStepName: data.leaseApplicationStepName?.id
      }
    });
  }



  function handleCloseSkuSwapModal() {
    setModalIsOpen('skuSwap', false);
    setSelectedOrderId('');
  }



  const loading = React.useMemo(() => _.some([
    filterQueryTuple,
    queryTuple,
    writeOrderToE1Tuple,
    closeOrderTuple,
    mockFulfillTuple
  ], t => t.loading), [
    filterQueryTuple,
    queryTuple,
    writeOrderToE1Tuple,
    closeOrderTuple,
    mockFulfillTuple
  ]);



  return (
    <>
      <activityTemplates.AdminActivityTemplate.FC elRightContent={params.salesChannel === 'thirdPartyLogistics' ? (
        <Button onClick={handleClick3plImports}>
          3PL Imports
        </Button>
      ) : undefined}>
        <LoadingOverlay.FC active={loading} />

        <SectionHeaderBox.FC>
          <FormContainerBox>
            <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
              <DateContainerBox>
                <StoreSelectFieldController
                  formControl={form.control}
                  label="Store"
                  fieldKey="store" />

                <DateFieldController
                  formControl={form.control}
                  label="Start"
                  fieldKey="after" />

                <DateFieldController
                  formControl={form.control}
                  label="End"
                  fieldKey="before" />

                <PaymentMethodSelectFieldController
                  formControl={form.control}
                  label="Payment Method"
                  fieldKey="paymentMethod" />

                <LeaseApplicationStepNameSelectFieldController
                  formControl={form.control}
                  label="Has Completed Step"
                  fieldKey="leaseApplicationStepName" />

                <TextFieldController
                  formControl={form.control}
                  label="Search"
                  fieldKey="omniFreeText" />
              </DateContainerBox>

              <SecondRowBox>
                <OrderStatusChipsContainer>
                  {_.map(filterQueryTuple.data.statuses, status => (
                    <OrderStatusChip.FC
                      variant="button"
                      key={status.name}
                      status={status.name}
                      isSelected={_.includes(selectedOrderStatuses, status.name)}
                      onClick={() => handleSelectOrderStatus(status.name)} />
                  ))}
                </OrderStatusChipsContainer>

                <ButtonBox>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={queryTuple.loading}>
                    Submit
                  </Button>

                  <Button
                    disabled={pageInfo.totalRows === 0}
                    onClick={handleClickExport}>
                    Export ({pageInfo.totalRows}) rows
                  </Button>
                </ButtonBox>
              </SecondRowBox>
            </Form.FC>
          </FormContainerBox>
        </SectionHeaderBox.FC>

        <Card>
          <CardContent>
            <TableComponent
              disableSorting
              paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
              onClickRowPopoverOption={handleClickPopoverOption}
              initSortOptions={{
                columnId: 'orderDate',
                direction: 'descending'
              }}
              columns={[
                {
                  id: 'orderNumber',
                  label: "#"
                },
                {
                  id: 'externalId',
                  label: "Post ID"
                },
                {
                  id: 'orderStatus',
                  label: "Status"
                },
                {
                  id: 'orderDate',
                  label: "Date Created"
                },
                {
                  id: 'age',
                  label: "Age"
                },
                {
                  id: 'lastUpdated',
                  label: "Updated At"
                },
                {
                  id: 'paymentMethod',
                  label: "Payment Method"
                },
                {
                  id: 'smartPayLeaseApplicationNumber',
                  label: "SmartPay Application #"
                },
                {
                  id: 'smartPayLastCompletedState',
                  label: "SmartPay State"
                },
                {
                  id: 'in47File',
                  label: "In 47 File"
                },
                {
                  id: 'in42File',
                  label: "In 42 File"
                },
                {
                  id: 'inShipStation',
                  label: "In ShipStation"
                },
                {
                  id: 'lineItemsMatch',
                  label: "Line Item Count"
                },
                {
                  id: 'totalsMatch',
                  label: "Totals match"
                },
                {
                  id: 'inDeviceLedger',
                  label: "Device Ledger"
                },
                {
                  id: 'hasShipped',
                  label: "Label Created"
                }
              ]}

              rows={_.map(queryTuple.data.orders, (order): types.IRow => ({
                id: order.orderId,
                popoverOptions: [
                  {
                    id: 'closeOrder',
                    show: order.canBeClosed,
                    label: "Close Order"
                  },
                  {
                    id: 'mockFulfill',
                    show: !isProd && !order.hasShipped,
                    label: "Mock Fulfill Order"
                  },
                  {
                    id: 'skuSwap',
                    label: "Create SKU Swap"
                  }
                ],
                columnId2Value: {
                  orderNumber: {
                    type: 'component',
                    sortableValue: order.orderNumber,
                    label: (
                      <Link
                        href={routeDirectory.admin.orderDetail.path(order.storeId, order.orderId)}
                        target="_blank">
                        {order.orderNumber}
                      </Link>
                    )
                  },
                  externalId: {
                    type: 'component',
                    sortableValue: order.externalId,
                    label: (
                      <Link
                        href={order.externalUrl}
                        target="_blank">
                        {order.externalId}
                      </Link>
                    )
                  },
                  orderStatus: {
                    type: 'component',
                    sortableValue: order.statusName,
                    label: <OrderStatusChip.FC status={order.statusName} />
                  },
                  orderDate: {
                    type: 'string',
                    label: order.orderDate.toFormat('yyyy-LL-dd @ hh:mm a')
                  },
                  age: {
                    type: 'string',
                    label: order.age.as('hours').toFixed(1) + ' hours'
                  },
                  lastUpdated: {
                    type: 'string',
                    label: order.cacheLastUpdatedTimestamp?.toFormat('yyyy-LL-dd @ hh:mm a') ?? order.orderDate.toFormat('yyyy-LL-dd @ hh:mm a')
                  },
                  lineItemsMatch: {
                    type: 'component',
                    sortableValue: order.lineItemCountE1Match && order.lineItemCountShipStationMatchesSource ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.lineItemCountE1Match && order.lineItemCountShipStationMatchesSource} />
                  },

                  totalsMatch: {
                    type: 'component',
                    sortableValue: order.orderTotalE1Match && order.orderTotalShipStationMatchesSource ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.orderTotalE1Match && order.orderTotalShipStationMatchesSource} />
                  },

                  inShipStation: {
                    type: 'component',
                    sortableValue: order.inShipStation ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.inShipStation} />
                  },

                  in47File: {
                    type: 'component',
                    sortableValue: order.in47File ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.in47File} />
                  },
                  in42File: {
                    type: 'component',
                    sortableValue: order.in42File ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.in42File} />
                  },

                  inDeviceLedger: {
                    type: 'component',
                    sortableValue: order.inDeviceLedger ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.inDeviceLedger} />
                  },

                  hasShipped: {
                    type: 'component',
                    sortableValue: order.hasShipped ? 1 : 0,
                    label: <StatusCheckIcon.FC success={order.hasShipped} />
                  },

                  paymentMethod: {
                    type: 'string',
                    label: order.paymentMethodDisplayName
                  },

                  smartPayLeaseApplicationNumber: {
                    type: 'string',
                    label: order.smartPayLeaseApplicationNumber
                  },

                  smartPayLastCompletedState: {
                    type: 'string',
                    label: order.lastCompletedLeaseApplicationStepDisplayName
                  }
                }
              }))} />
          </CardContent>
        </Card>
      </activityTemplates.AdminActivityTemplate.FC>

      {selectedOrderId && modalType2IsOpen.skuSwap && (
        <SkuSwapModal.FC
          orderId={selectedOrderId}
          onClose={handleCloseSkuSwapModal} />
      )}
    </>
  );
};
